import { useCallback, useRef, useState } from "react";

import type { ConfirmationResult, RecaptchaVerifier as RecaptchaVerifierType } from "firebase/auth";

import type { FirebaseClientConfig } from "./config";

// Mirror of legacy src/lib/firebase/use-firebase-phone-auth.ts (D21 1:1 port).
// Lazy-loads firebase/auth so the Vite chunk is split off the entry bundle.

/**
 * Why the last sendOtp / verifyOtp failed, for callers that word it in their
 * own (translated) copy — `error` holds the hook's English message. Read it
 * right after the call returned (`lastFailure()`): it is a ref, not state.
 */
export type FirebasePhoneAuthFailure =
  | "invalid_code"
  | "code_expired"
  | "no_pending"
  | "too_many_requests"
  | "invalid_phone"
  | "quota_exceeded"
  | "unknown";

export function useFirebasePhoneAuth() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const failureRef = useRef<FirebasePhoneAuthFailure | null>(null);
  const confirmationRef = useRef<ConfirmationResult | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifierType | null>(null);
  const recaptchaProjectRef = useRef<string | null>(null);

  const sendOtp = useCallback(
    async (
      phoneNumber: string,
      recaptchaContainerId: string,
      firebaseConfig?: FirebaseClientConfig | null,
    ) => {
      setError("");
      failureRef.current = null;
      setLoading(true);
      try {
        const { getFirebaseAuth, RecaptchaVerifier, signInWithPhoneNumber } =
          await import("./config");

        const auth = getFirebaseAuth(firebaseConfig);
        const projectId = firebaseConfig?.projectId || "default";

        if (recaptchaRef.current && recaptchaProjectRef.current !== projectId) {
          recaptchaRef.current.clear();
          recaptchaRef.current = null;
        }

        if (!recaptchaRef.current) {
          recaptchaRef.current = new RecaptchaVerifier(auth, recaptchaContainerId, {
            size: "invisible",
          });
          recaptchaProjectRef.current = projectId;
        }

        const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaRef.current);
        confirmationRef.current = confirmation;
        return true;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to send SMS";
        if (message.includes("too-many-requests")) {
          failureRef.current = "too_many_requests";
          setError("Too many attempts. Please wait a few minutes.");
        } else if (message.includes("invalid-phone-number")) {
          failureRef.current = "invalid_phone";
          setError("Invalid phone number format.");
        } else if (message.includes("quota-exceeded")) {
          failureRef.current = "quota_exceeded";
          setError("SMS quota exceeded. Please try again later.");
        } else {
          failureRef.current = "unknown";
          setError(message);
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const verifyOtp = useCallback(async (code: string) => {
    setError("");
    failureRef.current = null;
    setLoading(true);
    try {
      if (!confirmationRef.current) {
        failureRef.current = "no_pending";
        setError("No pending verification. Please resend the code.");
        return null;
      }
      const result = await confirmationRef.current.confirm(code);
      const idToken = await result.user.getIdToken();
      const phone = result.user.phoneNumber;
      return { idToken, phone };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Invalid code";
      if (message.includes("invalid-verification-code")) {
        failureRef.current = "invalid_code";
        setError("Invalid verification code. Please try again.");
      } else if (message.includes("code-expired")) {
        failureRef.current = "code_expired";
        setError("Code expired. Please request a new one.");
      } else if (message.includes("too-many-requests")) {
        failureRef.current = "too_many_requests";
        setError(message);
      } else {
        failureRef.current = "unknown";
        setError(message);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const cleanup = useCallback(() => {
    confirmationRef.current = null;
    if (recaptchaRef.current) {
      recaptchaRef.current.clear();
      recaptchaRef.current = null;
    }
    recaptchaProjectRef.current = null;
  }, []);

  const lastFailure = useCallback(() => failureRef.current, []);

  return { sendOtp, verifyOtp, cleanup, loading, error, setError, lastFailure };
}
